import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { Modal, Button } from 'react-bootstrap';

export interface ConfirmOptions {
  title: string;
  /** Body content — plain text or nodes. */
  body: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Use a destructive (red) confirm button for irreversible actions. */
  variant?: 'primary' | 'danger' | 'warning' | 'success';
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

interface PendingState extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

/**
 * App-wide confirmation dialog rendered as a react-bootstrap Modal (never the native
 * window.confirm). Exposes an async `confirm(options)` via `useConfirm()` that resolves to
 * true/false, so call sites read almost like the native API:
 *
 *   if (await confirm({ title, body })) { ...proceed... }
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingState | null>(null);
  // Keep the latest resolver so closing via backdrop/escape resolves false exactly once.
  const resolvedRef = useRef(false);

  const confirm = useCallback<ConfirmFn>((options) => {
    resolvedRef.current = false;
    return new Promise<boolean>((resolve) => {
      setPending({ ...options, resolve });
    });
  }, []);

  const settle = (ok: boolean) => {
    if (!pending || resolvedRef.current) return;
    resolvedRef.current = true;
    pending.resolve(ok);
    setPending(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal show={!!pending} onHide={() => settle(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title as="h5">{pending?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{pending?.body}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => settle(false)}>
            {pending?.cancelLabel ?? 'Cancel'}
          </Button>
          <Button variant={pending?.variant ?? 'primary'} onClick={() => settle(true)}>
            {pending?.confirmLabel ?? 'Confirm'}
          </Button>
        </Modal.Footer>
      </Modal>
    </ConfirmContext.Provider>
  );
}

/** Returns an async confirm(options) that resolves true when the user confirms. */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider');
  return ctx;
}
